import type { PublicRuntimeConfig } from "@/lib/config/publicRuntimeConfig";
import { onClientSessionInterrupted } from "@/lib/session/onClientSessionEnd";
import {
  SESSION_END_REASON,
  clearSessionEndReason,
  saveSessionEndReason,
} from "@/lib/session/sessionEndReasonStorage";
import {
  clearConsoleAccessToken,
  getConsoleAccessToken,
  isConsoleAccessTokenExpired,
  RENEW_OUTCOME,
  renewConsoleAccessToken,
} from "./consoleAccessToken";
import { getCognitoUserManager } from "./cognitoOidcSettings";
import { runCognitoLogin } from "./runCognitoLogin";
import { runCognitoLogout } from "./runCognitoLogout";

/**
 * - `RETRY`: the request can be sent again with the current token.
 * - `LOST`: the session is over and the user is leaving for Cognito.
 * - `UNAVAILABLE`: renewing was not possible right now (offline); the session is not judged.
 */
export const UNAUTHORIZED_OUTCOME = {
  RETRY: "retry",
  LOST: "lost",
  UNAVAILABLE: "unavailable",
} as const;

export type UnauthorizedOutcome =
  (typeof UNAUTHORIZED_OUTCOME)[keyof typeof UNAUTHORIZED_OUTCOME];

/**
 * - `ACTIVE`: normal operation; a 401 may renew or end the session.
 * - `ENDING`: the user is leaving for Cognito; polling skips its ticks.
 */
const SESSION_STATUS = {
  ACTIVE: "active",
  ENDING: "ending",
} as const;

type SessionState =
  | { status: typeof SESSION_STATUS.ACTIVE }
  | { status: typeof SESSION_STATUS.ENDING; redirect: Promise<boolean> };

const ACTIVE_STATE: SessionState = { status: SESSION_STATUS.ACTIVE };

let sessionState: SessionState = ACTIVE_STATE;

/**
 * - `LOGIN`: Cognito refused the renew; sign in again (SSO may skip the form).
 * - `LOGOUT`: the backend rejected a valid token; end the Cognito session too,
 *   so the user lands on the login screen instead of SSO handing back a token
 *   the backend rejects again.
 */
const SESSION_EXIT = {
  LOGIN: "login",
  LOGOUT: "logout",
} as const;

type SessionExit = (typeof SESSION_EXIT)[keyof typeof SESSION_EXIT];

/** True once the user is leaving for Cognito; polling skips its ticks. */
export const isConsoleSessionLost = (): boolean =>
  sessionState.status === SESSION_STATUS.ENDING;

const leaveForCognito = async (
  config: PublicRuntimeConfig,
  exit: SessionExit,
): Promise<boolean> => {
  clearConsoleAccessToken();
  onClientSessionInterrupted();
  if (exit === SESSION_EXIT.LOGOUT) {
    saveSessionEndReason(SESSION_END_REASON.TOKEN_REJECTED);
  }
  try {
    await (exit === SESSION_EXIT.LOGOUT
      ? runCognitoLogout(config)
      : runCognitoLogin(config));
    return true;
  } catch (e) {
    console.error("Failed to leave for Cognito after the session ended", e);
    clearSessionEndReason();
    sessionState = ACTIVE_STATE;
    return false;
  }
};

/**
 * Stops polling, clears client state and leaves for Cognito. Concurrent callers
 * share one redirect. Resolves true once the redirect has started, false if it
 * could not start (the app then keeps working and the next 401 tries again).
 */
const endConsoleSession = (
  config: PublicRuntimeConfig,
  exit: SessionExit,
): Promise<boolean> => {
  if (sessionState.status === SESSION_STATUS.ENDING) {
    return sessionState.redirect;
  }
  // Deferred so the state reads ENDING before any client state is cleared.
  const redirect = Promise.resolve().then(() => leaveForCognito(config, exit));
  sessionState = { status: SESSION_STATUS.ENDING, redirect };
  return redirect;
};

/** Cognito refused the renew: send the user to the Cognito login. */
const endConsoleSessionAndRedirectToLogin = (
  config: PublicRuntimeConfig,
): Promise<boolean> => endConsoleSession(config, SESSION_EXIT.LOGIN);

/** The backend rejected a valid token: log out of Cognito. */
export const endConsoleSessionAndLogOut = (
  config: PublicRuntimeConfig,
): Promise<boolean> => endConsoleSession(config, SESSION_EXIT.LOGOUT);

const toOutcome = (redirecting: boolean): UnauthorizedOutcome =>
  redirecting ? UNAUTHORIZED_OUTCOME.LOST : UNAUTHORIZED_OUTCOME.UNAVAILABLE;

const renewOrEndSession = async (
  config: PublicRuntimeConfig,
): Promise<UnauthorizedOutcome> => {
  const outcome = await renewConsoleAccessToken(config);
  if (outcome === RENEW_OUTCOME.RENEWED) {
    return UNAUTHORIZED_OUTCOME.RETRY;
  }
  if (outcome === RENEW_OUTCOME.UNAVAILABLE) {
    return UNAUTHORIZED_OUTCOME.UNAVAILABLE;
  }
  return toOutcome(await endConsoleSessionAndRedirectToLogin(config));
};

/**
 * The console API answered 401 to a request sent with `sentToken`. If the token
 * has been renewed since, the request just used a stale token: retry. If the
 * token has not expired, the backend rejected a valid token: log out of Cognito
 * rather than renewing. Otherwise renew once (shared by all callers); only a
 * Cognito rejection ends the session.
 */
export const handleConsoleUnauthorized = async (
  config: PublicRuntimeConfig,
  sentToken: string | null,
): Promise<UnauthorizedOutcome> => {
  if (isConsoleSessionLost()) {
    return UNAUTHORIZED_OUTCOME.LOST;
  }
  const current = getConsoleAccessToken();
  if (current != null && current !== sentToken) {
    return UNAUTHORIZED_OUTCOME.RETRY;
  }
  if (!(await isConsoleAccessTokenExpired(config))) {
    return toOutcome(await endConsoleSessionAndLogOut(config));
  }
  return renewOrEndSession(config);
};

let expiredListenerConfig: PublicRuntimeConfig | null = null;

const onAccessTokenExpired = (): void => {
  if (expiredListenerConfig != null && !isConsoleSessionLost()) {
    void renewOrEndSession(expiredListenerConfig);
  }
};

/** The access token expired and automatic renew did not replace it. */
export const attachConsoleSessionLostListeners = (
  config: PublicRuntimeConfig,
): void => {
  const { events } = getCognitoUserManager(config);
  events.removeAccessTokenExpired(onAccessTokenExpired);
  expiredListenerConfig = config;
  events.addAccessTokenExpired(onAccessTokenExpired);
};

/** Test helper. */
export const resetConsoleSessionLostForTests = (): void => {
  sessionState = ACTIVE_STATE;
  expiredListenerConfig = null;
};
